import { Box, Button, Slider, TextField, Typography } from "@mui/material";
import { palette } from "../../themes/theme";
import { useEffect, useState } from "react";
import DarkPrimaryButton from "../PrimaryButton/DarkPrimaryButton";
import { CustomDraggable } from "../Draggable";
import { DefaultValues, hexToRgba } from "../../utils/helper";
import ColorPicker from "../ColorPicker";
import { CommonUtils } from "./utilities/CommonUtils";

export const ShapeStyleSetting = ({
  mapRef,
  shapeSettings,
  setShapeSettings,
  setMarkers,
  resetAllOnceChanged,
  markerRef,
}) => {
  const [shapeStyleLocally, setShapeStyleLocally] = useState({
    isBackgroundFilled:
      shapeSettings.selectedShape?.style.isBackgroundFilled ?? true,
    isBorderFilled: shapeSettings.selectedShape?.style.isBorderFilled ?? true,
    backgroundColor: shapeSettings.selectedShape?.style.backgroundColor,
    borderColor: shapeSettings.selectedShape?.style.borderColor,
    borderWidth: shapeSettings.selectedShape?.style.borderWidth ?? 1,
    opacity: shapeSettings.selectedShape?.style.opacity,
  });

  const iTag = document.getElementById(shapeSettings.selectedShape?.id);

  const changeStyle = (type, event) => {
    const tempStyle = { ...shapeStyleLocally };
    switch (type) {
      case "background-color":
        tempStyle.backgroundColor = event;
        iTag.dataset.backgroundColor = hexToRgba(event, tempStyle.opacity);
        break;
      case "background-color-transparent":
        tempStyle.isBackgroundFilled = event.target.checked;
        break;
      case "transparency":
        tempStyle.opacity = event.target.value;
        iTag.dataset.backgroundColor = hexToRgba(
          tempStyle.backgroundColor,
          tempStyle.opacity
        );
        break;
      case "border-color":
        tempStyle.borderColor = event;
        iTag.dataset.borderColor = hexToRgba(event, 1);
        break;
      case "border-color-transparent":
        tempStyle.isBorderFilled = event.target.checked;
        break;
      case "border-width":
        tempStyle.borderWidth = event.target.value;
        iTag.dataset.borderWidth = String(tempStyle.borderWidth);
        if (tempStyle.borderWidth > 0) {
          iTag.style.borderWidth = String(tempStyle.borderWidth) + "px";
          iTag.style.setProperty(
            "-webkit-text-stroke-width",
            String(tempStyle.borderWidth) + "px"
          );
        }
        break;
      default:
        break;
    }
    iTag.style.color = tempStyle.isBackgroundFilled
      ? hexToRgba(tempStyle.backgroundColor, parseFloat(tempStyle.opacity))
      : "rgba(0,0,0,0)";
    iTag.style.setProperty(
      "-webkit-text-stroke-color",
      tempStyle.isBorderFilled ? tempStyle.borderColor : "rgba(0,0,0,0)"
    );
    setShapeStyleLocally(tempStyle);
  };
  const handleRemove = () => {
    CommonUtils.RemoveMarkerFromList({
      id: shapeSettings.selectedShape?.id || null,
      markerRef,
    });
    setMarkers(markerRef.current);
    setShapeSettings({ ...DefaultValues.defaultShapeSetting });
    mapRef.getCanvas().style.cursor = "grab";
    resetAllOnceChanged();
  };

  const handleOk = () => {
    setShapeSettings({ ...DefaultValues.defaultShapeSetting });
    mapRef.getCanvas().style.cursor = "grab";
    resetAllOnceChanged();
  };

  useEffect(() => {
    setShapeStyleLocally({
      isBackgroundFilled:
        shapeSettings.selectedShape?.style.isBackgroundFilled ?? true,
      isBorderFilled: shapeSettings.selectedShape?.style.isBorderFilled ?? true,
      backgroundColor: shapeSettings.selectedShape?.style.backgroundColor,
      borderColor: shapeSettings.selectedShape?.style.borderColor,
      borderWidth: shapeSettings.selectedShape?.style.borderWidth ?? 1,
      opacity: shapeSettings.selectedShape?.style.opacity,
    });
  }, [shapeSettings]);

  return (
    <CustomDraggable
      width={250}
      height={320}
      headerTitle={"Shape Settings"}
      disableOverflow={true}
      customFooter={
        <>
          <Button
            variant="outlined"
            sx={{ fontWeight: "bold" }}
            onClick={handleOk}
          >
            Ok
          </Button>
          <Button
            variant="outlined"
            sx={{ fontWeight: "bold" }}
            onClick={handleRemove}
          >
            Remove
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            color: palette.text.dark,
            fontSize: 15,
          }}
        >
          Background Settings
        </Typography>
        <ColorPicker
          title={"Fill"}
          checked={shapeStyleLocally.isBackgroundFilled}
          color={shapeStyleLocally.backgroundColor}
          setColor={changeStyle}
          type={"background-color"}
        />
        <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
          Transparency:
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Slider
            min={0}
            step={1}
            max={100}
            defaultValue={100}
            valueLabelDisplay="auto"
            onChange={(e) => changeStyle("transparency", e)}
            sx={{ width: "85%" }}
          />
        </Box>
        <Typography
          sx={{
            color: palette.text.dark,
            fontSize: 15,
          }}
        >
          Border Settings
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <ColorPicker
            // title={"Border"}
            checked={shapeStyleLocally.isBorderFilled}
            color={shapeStyleLocally.borderColor}
            setColor={changeStyle}
            type={"border-color"}
          />
          <TextField
            type="number"
            sx={{
              width: "65px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                color: "black",
                borderRadius: "4px",
                "& fieldset": {
                  borderColor: "black", // default border
                },
                "&:hover fieldset": {
                  borderColor: "black", // on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "black", // on focus
                },
              },
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  display: "none",
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
              "& .MuiInputLabel-root": {
                color: "black", // label color
              },
            }}
            label="Size"
            defaultValue={shapeStyleLocally.borderWidth}
            onChange={(e) => changeStyle("border-width", e)}
          />
        </Box>
      </Box>
    </CustomDraggable>
  );
};
